/**
 * EVENT DETAIL MODAL
 * 
 * Shows full event details when clicking an event in calendar view.
 * Includes Add to Calendar dropdown and Remind Me buttons.
 * Location: src/components/events/EventDetailModal.tsx
 */

import { useState } from 'react';
import { X, Calendar, Clock, ExternalLink, Tag, Sparkles, CalendarPlus, Bell, Repeat, ChevronDown, Edit2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CalendarEvent } from '../../types/event';
import {
  getEventTypeInfo,
  formatEventDate,
  formatEventTime,
  isEventLive,
  downloadEventICS,
  generateGoogleCalendarUrl
} from '../../types/event';
import { formatTimeWithConversion } from '../../constants/timezones';

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  onSetReminder?: (event: CalendarEvent) => void;
  onEdit?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  canManageEvent?: boolean;  // True if current user can edit/delete
}

export default function EventDetailModal({
  isOpen,
  onClose,
  event,
  onSetReminder,
  onEdit,
  onDelete,
  canManageEvent = false
}: EventDetailModalProps) {
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);
  
  if (!isOpen || !event) return null;

  const typeInfo = getEventTypeInfo(event.eventType);
  const dateStr = formatEventDate(event);
  const timeStr = formatEventTime(event);
  const isLive = isEventLive(event);
  const googleCalendarUrl = generateGoogleCalendarUrl(event);
  
  // Get time conversion for viewers in different timezones
  const timeConversion = event.startTime && event.timezone
    ? formatTimeWithConversion(event.startDate.toDate(), event.startTime, event.timezone)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass-card p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-slate-700 rounded-lg transition-default"
        >
          <X className="icon-base text-glass-secondary" />
        </button>

        {/* Event type badge + Live indicator */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${typeInfo.color} text-white`}>
            {typeInfo.emoji} {typeInfo.label}
          </span>
          {isLive && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-sm rounded-full animate-pulse">
              🔴 LIVE NOW
            </span>
          )}
          {event.recurrence && event.recurrence.pattern !== 'none' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-300 text-sm rounded-full">
              <Repeat className="w-3 h-3" />
              {event.recurrence.pattern === 'weekly' ? 'Weekly' : 'Monthly'}
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-glass mb-2 pr-8">
          {event.title}
        </h2>

        {/* Brand */}
        <Link
          to={`/brands/${event.brandSlug}`}
          className="inline-flex items-center gap-2 text-glass-secondary hover:text-pink-300 transition-default mb-4"
          onClick={onClose}
        >
          {event.brandLogo ? (
            <img
              src={event.brandLogo}
              alt=""
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {event.brandName.charAt(0)}
              </span>
            </div>
          )}
          {event.brandName}
        </Link>

        {/* Date & Time */}
        <div className="bg-slate-800/60 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 text-glass">
            <Calendar className="icon-base text-pink-400" />
            <div>
              <p className="font-medium">{dateStr}</p>
              {timeConversion ? (
                <div className="text-sm">
                  <p className="text-glass-secondary flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeConversion.original}
                  </p>
                  {timeConversion.showBoth && (
                    <p className="text-pink-400 text-xs mt-1">
                      ({timeConversion.local} your time)
                    </p>
                  )}
                </div>
              ) : timeStr && (
                <p className="text-sm text-glass-secondary flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {timeStr}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <p className="text-glass-secondary mb-4">
            {event.description}
          </p>
        )}

        {/* Discount info */}
        {event.discountInfo && (
          <div className="flex items-center gap-2 text-green-400 mb-4 bg-green-500/10 rounded-lg p-3">
            <Tag className="icon-sm" />
            <span className="font-medium">{event.discountInfo}</span>
          </div>
        )}

        {/* Products teased */}
        {event.productsTeased && event.productsTeased.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-glass-secondary mb-2">Products:</p>
            <div className="flex flex-wrap gap-2">
              {event.productsTeased.map((product, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-slate-700/50 rounded-full text-sm text-glass-secondary"
                >
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  {product}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-700">
          {/* Add to Calendar Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowCalendarMenu(!showCalendarMenu)}
              className="btn-secondary flex items-center gap-2"
            >
              <CalendarPlus className="icon-sm" />
              Add to Calendar
              <ChevronDown className={`icon-sm transition-transform ${showCalendarMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown menu */}
            {showCalendarMenu && (
              <>
                {/* Invisible backdrop to close menu */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowCalendarMenu(false)}
                />
                <div className="absolute left-0 top-full mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 overflow-hidden">
                  <button
                    onClick={() => {
                      downloadEventICS(event);
                      setShowCalendarMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-700 transition-default flex items-center gap-3"
                  >
                    <span className="text-lg">📅</span>
                    <div>
                      <p className="text-sm font-medium text-glass">Download .ics</p>
                      <p className="text-xs text-glass-muted">Apple, Outlook, any calendar</p>
                    </div>
                  </button>
                  <a
                    href={googleCalendarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowCalendarMenu(false)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-700 transition-default flex items-center gap-3 border-t border-slate-700"
                  >
                    <span className="text-lg">📆</span>
                    <div>
                      <p className="text-sm font-medium text-glass">Google Calendar</p>
                      <p className="text-xs text-glass-muted">Opens in new tab</p>
                    </div>
                  </a>
                </div>
              </>
            )}
          </div>

          {/* Remind Me */}
          {onSetReminder && (
            <button
              onClick={() => {
                onSetReminder(event);
                onClose();
              }}
              className="btn-secondary flex items-center gap-2"
            >
              <Bell className="icon-sm" />
              Remind Me
            </button>
          )}

          {/* Shop Now */}
          {event.shopUrl && (
            <a
              href={event.shopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2"
            >
              Shop Now
              <ExternalLink className="icon-sm" />
            </a>
          )}

          {/* Social/Live link */}
          {event.socialUrl && (
            <a
              href={event.socialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
            >
              {event.eventType === 'live_sale' ? 'Join Live' : 'View'}
              <ExternalLink className="icon-sm" />
            </a>
          )}
        </div>
        
        {/* Edit/Delete for brand owners */}
        {canManageEvent && (onEdit || onDelete) && (
          <div className="flex gap-2 pt-4 mt-4 border-t border-slate-700">
            {onEdit && (
              <button
                onClick={() => {
                  onEdit(event);
                  onClose();
                }}
                className="flex-1 btn-secondary flex items-center justify-center gap-2"
              >
                <Edit2 className="icon-sm" />
                Edit Event
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to delete "${event.title}"? This cannot be undone.`)) {
                    onDelete(event);
                    onClose();
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-default flex items-center justify-center gap-2"
              >
                <Trash2 className="icon-sm" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
